import { common } from '../common.js';
import type { IProgram } from '../types.js';

/** People, forms and documents most permanent residence applications share. */
function prBase(source: number): IProgram['items'] {
  return [
    { id: 'personalChecklist', label: 'IRCC document checklist (from the PR portal), filled in', section: 'Forms', forRoles: 'application', source },
    { id: 'imm0008', label: 'Generic Application Form for Canada (online)', form: 'IMM 0008', section: 'Forms', forRoles: ['principal'], source },
    { id: 'imm5669', label: 'Schedule A, Background/Declaration (online)', form: 'IMM 5669', section: 'Forms', forRoles: ['principal', 'spouse', 'dependant'], minAge: 18, source },
    { id: 'imm5406', label: 'Additional Family Information (online)', form: 'IMM 5406', section: 'Forms', forRoles: ['principal', 'spouse', 'dependant'], minAge: 18, source },
    common.representative(source),
    common.passport(source, ['principal', 'spouse', 'dependant']),
    common.birth(source, ['principal', 'spouse', 'dependant']),
    common.photos(source, ['principal', 'spouse', 'dependant']),
    common.police(source, ['principal', 'spouse', 'dependant']),
    common.translations(source),
    common.fees(source)
  ];
}

const FAMILY_ROLES: IProgram['roles'] = [
  { id: 'principal', label: 'Principal applicant', min: 1, max: 1 },
  { id: 'spouse', label: 'Spouse or common-law partner', min: 0, max: 1 },
  { id: 'dependant', label: 'Dependent child', min: 0, max: 15 }
];
const SECTIONS: string[] = ['Forms', 'Program', 'Identity and civil documents', 'Photos and police certificates', 'Fees'];

export const ATLANTIC: IProgram = {
  id: 'atlantic-immigration',
  name: 'Atlantic Immigration Program',
  short: 'Atlantic Immigration Program',
  family: 'economic',
  status: 'active',
  version: '2026-10-04',
  review: 'pending',
  sources: [{ title: 'IRCC: Atlantic Immigration Program', url: 'https://www.canada.ca/en/immigration-refugees-citizenship/services/immigrate-canada/atlantic-immigration.html', retrieved: '2026-10-04' }],
  questions: [],
  roles: FAMILY_ROLES,
  sections: SECTIONS,
  items: [
    { id: 'endorsement', label: 'Provincial endorsement certificate', section: 'Program', forRoles: ['principal'], source: 0 },
    { id: 'jobOffer', label: 'Job offer from a designated employer', section: 'Program', forRoles: ['principal'], source: 0 },
    { id: 'language', label: 'Language test results', section: 'Program', forRoles: ['principal'], source: 0 },
    { id: 'education', label: 'Proof of education (or an educational credential assessment)', section: 'Program', forRoles: ['principal'], source: 0 },
    { id: 'experience', label: 'Proof of work experience', section: 'Program', forRoles: ['principal'], source: 0 },
    { id: 'funds', label: 'Proof of funds (unless already working in Canada on a valid permit)', section: 'Program', forRoles: ['principal'], source: 0 },
    ...prBase(0)
  ],
  deadlines: []
};

export const CAREGIVERS: IProgram = {
  id: 'caregivers',
  name: 'Home care worker immigration (caregiver pilots)',
  short: 'Caregiver pilot',
  family: 'economic',
  status: 'active',
  version: '2026-10-04',
  review: 'pending',
  sources: [{ title: 'IRCC: Caregivers', url: 'https://www.canada.ca/en/immigration-refugees-citizenship/services/immigrate-canada/caregivers.html', retrieved: '2026-10-04' }],
  questions: [],
  roles: FAMILY_ROLES,
  sections: SECTIONS,
  items: [
    { id: 'jobOffer', label: 'Job offer for full-time home care work', section: 'Program', forRoles: ['principal'], source: 0 },
    { id: 'language', label: 'Language test results', section: 'Program', forRoles: ['principal'], source: 0 },
    { id: 'education', label: 'Proof of education (or an educational credential assessment)', section: 'Program', forRoles: ['principal'], source: 0 },
    { id: 'experience', label: 'Proof of recent home care work experience, if claimed', section: 'Program', forRoles: ['principal'], source: 0 },
    ...prBase(0)
  ],
  deadlines: []
};

export const RURAL_FRANCOPHONE: IProgram = {
  id: 'rural-francophone-pilots',
  name: 'Rural Community and Francophone Community Immigration Pilots',
  short: 'Community immigration pilot',
  family: 'economic',
  status: 'active',
  version: '2026-10-04',
  review: 'pending',
  sources: [{ title: 'IRCC: Rural and Francophone community immigration pilots', url: 'https://www.canada.ca/en/immigration-refugees-citizenship/services/immigrate-canada/rural-franco-pilots.html', retrieved: '2026-10-04' }],
  questions: [],
  roles: FAMILY_ROLES,
  sections: SECTIONS,
  items: [
    { id: 'recommendation', label: 'Community recommendation', section: 'Program', forRoles: ['principal'], source: 0 },
    { id: 'jobOffer', label: 'Job offer from a designated employer in the community', section: 'Program', forRoles: ['principal'], source: 0 },
    { id: 'language', label: 'Language test results', section: 'Program', forRoles: ['principal'], source: 0 },
    { id: 'education', label: 'Proof of education (or an educational credential assessment)', section: 'Program', forRoles: ['principal'], source: 0 },
    { id: 'experience', label: 'Proof of work experience', section: 'Program', forRoles: ['principal'], source: 0 },
    { id: 'funds', label: 'Proof of funds (unless already working in Canada)', section: 'Program', forRoles: ['principal'], source: 0 },
    ...prBase(0)
  ],
  deadlines: []
};

export const START_UP_VISA: IProgram = {
  id: 'start-up-visa',
  name: 'Start-up Visa',
  short: 'Start-up Visa',
  family: 'economic',
  status: 'active',
  version: '2026-10-04',
  review: 'pending',
  sources: [{ title: 'IRCC: Start-up Visa', url: 'https://www.canada.ca/en/immigration-refugees-citizenship/services/immigrate-canada/start-visa.html', retrieved: '2026-10-04' }],
  questions: [],
  roles: FAMILY_ROLES,
  sections: SECTIONS,
  items: [
    { id: 'commitment', label: 'Letter of support (commitment certificate) from a designated organization', section: 'Program', forRoles: ['principal'], source: 0 },
    { id: 'business', label: 'Proof of the qualifying business and ownership', section: 'Program', forRoles: ['principal'], source: 0 },
    { id: 'language', label: 'Language test results', section: 'Program', forRoles: ['principal'], source: 0 },
    { id: 'funds', label: 'Proof of funds', section: 'Program', forRoles: ['principal'], source: 0 },
    ...prBase(0)
  ],
  deadlines: []
};
